import {
  fetchWithErrorHandling,
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {useMergeBoxPageDataQueryKey} from '../../page-data/loaders/use-merge-box-page-data'
import {MergeError} from '../../helpers/merge-error'
import type {PullRequestMergeMethod} from '../../types'

/**
 * Enables auto-merge for stacked pull requests, starting from the given pull request and will auto-merge
 * all down stack PRs that are not merged, merging, queued or closed.
 *
 * To run additional callbacks, you can pass any supported callback option to the `mutate` function when it's
 * called, including `onSuccess`, `onError`, and `onSettled`. These additional callbacks will run after the
 * callbacks defined in the hook below.
 *
 * For more details, see https://tanstack.com/query/latest/docs/framework/react/guides/mutations#mutation-side-effects
 *
 * @example
 * ```ts
 * const {mutate} = useAutoMergeStackMutation()
 *
 * // Required arguments
 * const mutationArguments = {mergeMethod: 'SQUASH'}
 *
 * // Optional callbacks will run after callbacks defined in useAutoMergeStackMutation hook
 * const mutationCallbacks = {
 *   onError: error => console.log(error.message),
 *   onSuccess: (data) => console.log(data),
 * }
 *
 * mutate(mutationArguments, mutationCallbacks)
 * ```
 */
type MutationData = {
  authorEmail?: string | null | undefined
  commitMessage?: string | null | undefined
  commitTitle?: string | null | undefined
  mergeMethod: PullRequestMergeMethod
}

export function useAutoMergeStackMutation({
  onError,
  onSuccess,
}: {
  onError: (error: Error) => void
  onSuccess?: () => void
}) {
  const apiURL = usePageDataUrl(PageData.enqueueStack)
  const mergeBoxQueryKey = useMergeBoxPageDataQueryKey()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({authorEmail, commitMessage, commitTitle, mergeMethod}: MutationData) => {
      const response = await fetchWithErrorHandling(apiURL, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: {
          authorEmail,
          commitMessage,
          commitTitle,
          mergeAction: 'AUTO_MERGE',
          mergeMethod,
        },
      })
      const json = await parseJSONWithBetterErrors(response)
      const predefinedError = new MergeError(
        json?.error || 'Unknown error occurred',
        json?.metadata?.ruleErrors || [],
        response.status,
      )
      throwErrorsIfBadResponse(response, json, predefinedError)
      return json
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: mergeBoxQueryKey}, {cancelRefetch: false})
      return onSuccess?.()
    },
    onError: (e: Error) => onError(e),
  })
}
